import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { X, Plus, Sparkle } from 'phosphor-react';
import { theme } from '../../styles/theme';
import { useTranslation } from '../../i18n';

interface FoodFactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  foodData: FoodAnalysis | null;
  onTalkWithSoul: () => void;
  onLogToCalories?: (mealType: 'breakfast' | 'lunch' | 'dinner' | 'snacks') => void;
  showLogToCalories?: boolean;
  insightLoading?: boolean;
  insightText?: string | null;
  insightError?: boolean;
  onRetryInsight?: () => void;
}

export interface FoodAnalysis {
  name: string;
  servingSize: string;
  calories: number;
  macros: {
    carbs: { grams: number; percentage: number };
    protein: { grams: number; percentage: number };
    fat: { grams: number; percentage: number };
  };
  nutrients: {
    fiber: number;
    sugars: number;
  };
}

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { transform: scale(0.5); opacity: 0.3; }
  40% { transform: scale(1); opacity: 1; }
`;

const insightFadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: ${theme.zIndex.modal + 1};
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: flex-end;
  animation: ${fadeIn} 0.3s ease-out;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  background: var(--color-bg);
  border-radius: 24px 24px 0 0;
  padding: 24px;
  padding-bottom: max(24px, env(safe-area-inset-bottom));
  animation: ${slideUp} 0.3s ease-out;
  position: relative;
  max-height: 85vh;
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-elevated);
  border-radius: ${theme.borderRadius.full};
  color: var(--color-text);
  transition: all ${theme.transitions.fast};

  &:hover { background: var(--color-border-subtle); }
  &:active { transform: scale(0.95); }
`;

const FoodName = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const ServingSize = styled.p`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0 0 24px 0;
`;

const MacrosContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 32px;
`;

const MacroCircle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const CircleWrapper = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
`;

const CircleBackground = styled.svg`
  transform: rotate(-90deg);
  width: 100%;
  height: 100%;
`;

const CircleProgress = styled.circle<{ $color: string }>`
  fill: none;
  stroke: ${({ $color }) => $color};
  stroke-width: 6;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.5s ease;
`;

const CircleTrack = styled.circle`
  fill: none;
  stroke: var(--color-border-subtle);
  stroke-width: 6;
`;

const CircleText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
`;

const MacroLabel = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 13px;
  color: var(--color-text);
`;

const Divider = styled.div`
  height: 1px;
  background: var(--color-border-subtle);
  margin: 24px 0;
`;

const CaloriesSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const CaloriesLabel = styled.div`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
`;

const CaloriesValue = styled.div`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
`;

const NutrientsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
`;

const NutrientRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NutrientLabel = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  color: var(--color-text);
`;

const NutrientValue = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text);
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TalkButton = styled.button`
  width: 100%;
  padding: 16px;
  background: transparent;
  border: 1.5px solid var(--color-primary);
  border-radius: 12px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 16px;
  font-weight: 600;
  color: var(--color-primary);
  transition: all ${theme.transitions.fast};

  &:hover {
    background: rgba(131, 76, 201, 0.1);
    transform: translateY(-2px);
  }
  &:active { transform: translateY(0); }
`;

const LogButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  padding: 16px;
  background: ${({ $disabled }) => ($disabled ? 'rgba(131, 76, 201, 0.4)' : 'var(--color-primary)')};
  border-radius: 12px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
  transition: all ${theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};

  &:hover { opacity: ${({ $disabled }) => ($disabled ? 1 : 0.9)}; transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateY(-2px)')}; }
  &:active { transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateY(0)')}; }
`;

const MealTypeSelector = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const MealTypeButton = styled.button<{ $selected: boolean }>`
  flex: 1;
  padding: 10px 8px;
  border-radius: 8px;
  border: 1px solid ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'var(--color-border)')};
  background: ${({ $selected }) => ($selected ? 'rgba(131, 76, 201, 0.2)' : 'transparent')};
  color: ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'var(--color-text)')};
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover { border-color: var(--color-primary); }
`;

// Insight loading & result UI
const InsightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0 8px;
  gap: 14px;
  animation: ${insightFadeIn} 0.3s ease-out;
`;

const InsightLoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InsightLoadingText = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  color: var(--color-text-secondary);
`;

const DotsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
`;

const Dot = styled.span<{ $delay: number }>`
  display: inline-block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--color-primary-light);
  animation: ${dotPulse} 1.2s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}ms;
`;

const InsightCard = styled.div`
  width: 100%;
  background: rgba(131, 76, 201, 0.12);
  border: 1px solid rgba(131, 76, 201, 0.25);
  border-radius: 14px;
  padding: 16px;
  animation: ${insightFadeIn} 0.4s ease-out;
`;

const InsightCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
`;

const InsightCardTitle = styled.div`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 13px;
  font-weight: 600;
  color: var(--color-primary-light);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InsightCardText = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  color: var(--color-text);
  line-height: 1.55;
`;


const MACRO_COLORS = {
  carbs: '#FF6B6B',
  protein: '#FF8BD1',
  fat: '#FFD93D',
};

const MEAL_TYPE_KEYS: Array<{ key: 'breakfast' | 'lunch' | 'dinner' | 'snacks'; labelKey: string }> = [
  { key: 'breakfast', labelKey: 'food.breakfast' },
  { key: 'lunch', labelKey: 'food.lunch' },
  { key: 'dinner', labelKey: 'food.dinner' },
  { key: 'snacks', labelKey: 'food.snacks' },
];

export const FoodFactsModal: React.FC<FoodFactsModalProps> = ({
  isOpen,
  onClose,
  foodData,
  onTalkWithSoul,
  onLogToCalories,
  showLogToCalories = false,
  insightLoading = false,
  insightText = null,
  insightError = false,
  onRetryInsight,
}) => {
  const { t } = useTranslation();
  const [selectedMealType, setSelectedMealType] = useState<'breakfast' | 'lunch' | 'dinner' | 'snacks'>('breakfast');
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Auto-select meal type based on time of day
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 11) setSelectedMealType('breakfast');
    else if (hour >= 11 && hour < 15) setSelectedMealType('lunch');
    else if (hour >= 15 && hour < 21) setSelectedMealType('dinner');
    else setSelectedMealType('snacks');
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on escape key (listener only attached while open)
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Move focus into the dialog on open, restore it on close/unmount
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    modalRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleLog = () => {
    if (!onLogToCalories) return;
    onLogToCalories(selectedMealType);
  };

  if (!foodData) return null;

  const renderMacroCircle = (label: string, percentage: number, color: string) => {
    const radius = 37;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <MacroCircle>
        <CircleWrapper>
          <CircleBackground>
            <CircleTrack cx="40" cy="40" r={radius} />
            <CircleProgress
              cx="40" cy="40" r={radius}
              $color={color}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
            />
          </CircleBackground>
          <CircleText>{percentage}%</CircleText>
        </CircleWrapper>
        <MacroLabel>{label}</MacroLabel>
      </MacroCircle>
    );
  };

  return (
    <Overlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <Modal
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="food-facts-title"
        tabIndex={-1}
      >
        <Header>
          <Title id="food-facts-title">{t('food.facts')}</Title>
          <CloseButton onClick={onClose} aria-label="Close">
            <X size={20} weight="bold" />
          </CloseButton>
        </Header>

        <FoodName>{foodData.name}</FoodName>
        <ServingSize>{foodData.servingSize}</ServingSize>

        <MacrosContainer>
          {renderMacroCircle(t('food.carbs'), foodData.macros.carbs.percentage, MACRO_COLORS.carbs)}
          {renderMacroCircle(t('food.protein'), foodData.macros.protein.percentage, MACRO_COLORS.protein)}
          {renderMacroCircle(t('food.fat'), foodData.macros.fat.percentage, MACRO_COLORS.fat)}
        </MacrosContainer>

        <Divider />

        <CaloriesSection>
          <CaloriesLabel>{t('food.calories')}</CaloriesLabel>
          <CaloriesValue>{foodData.calories} kcal</CaloriesValue>
        </CaloriesSection>

        <NutrientsList>
          <NutrientRow>
            <NutrientLabel>{t('food.carbs')}</NutrientLabel>
            <NutrientValue>{foodData.macros.carbs.grams} g</NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>{t('food.fiber')}</NutrientLabel>
            <NutrientValue>{foodData.nutrients.fiber} g</NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>{t('food.sugars')}</NutrientLabel>
            <NutrientValue>{foodData.nutrients.sugars} g</NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>{t('food.protein')}</NutrientLabel>
            <NutrientValue>{foodData.macros.protein.grams} g</NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>{t('food.fat')}</NutrientLabel>
            <NutrientValue>{foodData.macros.fat.grams} g</NutrientValue>
          </NutrientRow>
        </NutrientsList>

        {/* Insight loading animation — shown immediately when modal opens */}
        {insightLoading && (
          <InsightSection>
            <InsightLoadingRow>
              <Sparkle size={16} weight="fill" color={'var(--color-primary-light)'} />
              <InsightLoadingText>{t('food.evaluating')}</InsightLoadingText>
              <DotsRow>
                <Dot $delay={0} />
                <Dot $delay={180} />
                <Dot $delay={360} />
              </DotsRow>
            </InsightLoadingRow>
          </InsightSection>
        )}

        {/* Insight result card */}
        {!insightLoading && insightText && (
          <InsightSection>
            <InsightCard>
              <InsightCardHeader>
                <Sparkle size={16} weight="fill" color={'var(--color-primary-light)'} />
                <InsightCardTitle>{t('food.soulInsight')}</InsightCardTitle>
              </InsightCardHeader>
              <InsightCardText>{insightText}</InsightCardText>
            </InsightCard>
          </InsightSection>
        )}

        {/* Insight error — retry */}
        {!insightLoading && !insightText && insightError && (
          <InsightSection>
            <InsightLoadingRow style={{ cursor: onRetryInsight ? 'pointer' : 'default' }} onClick={onRetryInsight}>
              <Sparkle size={16} weight="fill" color="var(--color-border)" />
              <InsightLoadingText style={{ color: 'var(--color-text-muted)' }}>
                {t('food.insightRetry')}
              </InsightLoadingText>
            </InsightLoadingRow>
          </InsightSection>
        )}

        {/* Log + Talk buttons */}
        {showLogToCalories && onLogToCalories && (
          <>
            <MealTypeSelector>
              {MEAL_TYPE_KEYS.map((meal) => (
                <MealTypeButton
                  key={meal.key}
                  $selected={selectedMealType === meal.key}
                  onClick={() => setSelectedMealType(meal.key)}
                >
                  {t(meal.labelKey)}
                </MealTypeButton>
              ))}
            </MealTypeSelector>
            <ButtonsContainer>
              <LogButton onClick={handleLog}>
                <Plus size={18} weight="bold" />
                {t('food.logToCalories')}
              </LogButton>
              <TalkButton onClick={onTalkWithSoul}>{t('food.talkWithSoul')}</TalkButton>
            </ButtonsContainer>
          </>
        )}
        {!showLogToCalories && (
          <TalkButton onClick={onTalkWithSoul}>{t('food.talkWithSoul')}</TalkButton>
        )}
      </Modal>
    </Overlay>
  );
};
